import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { patchUserProjectPreference } from '../services/patchUserProjectPreference.service';
import { getProjectsActivityFilterCacheKey } from '../utils/getProjectsActivityFilterCacheKey';
import { getUserProjectPreferenceKey } from '../utils/userProjectPreferences';

export const applyOptimisticUserProjectPreferenceUpdate = createAction(
  'projects/applyOptimisticUserProjectPreferenceUpdate'
);

/**
 * Saves the user's preference for a project: any of `favorite`, `hidden`,
 * `sensorDisplay` and `dashboardLayout` given (left out, the stored value
 * stays), merged into the stored preference, which is sent whole.
 *
 * Shown at once (applyOptimisticUserProjectPreferenceUpdate) and put back
 * when the save fails. Only `favorite` / `hidden` move the project in the
 * project list (a new `updatedAt`, a favourite to the front of the latest
 * projects); the dashboard's choices keep its place.
 *
 * Fulfilled with the preference as the API saved it. Rejected with
 * `{ status, message }`: the API's HTTP status, or `status: null` when no
 * answer came.
 */
export const updateUserProjectPreference = createAsyncThunk(
  'projects/updateUserProjectPreference',
  async (
    {
      project,
      favorite,
      hidden,
      // How the project dashboard shows the sensor readings, a
      // SENSOR_DISPLAY (utils/userProjectPreferences).
      sensorDisplay,
      // The user's own dashboard layout for the project, `{version, items}`;
      // null clears it, so the project's layout shows again.
      dashboardLayout,
      activeEmployerInfo: overrideActiveEmployerInfo,
    },
    { dispatch, getState, requestId, rejectWithValue }
  ) => {
    const activeEmployerInfo =
      overrideActiveEmployerInfo || selectUserActiveEmployerInfo(getState());
    const now = dayjs().toISOString();
    // Whether the update moves the project in the project list. The
    // dashboard's sensor display and layout do not: the project stays where
    // it is among the latest projects, and among the favourites, which are
    // ordered by updatedAt.
    const movesInProjectList = favorite !== undefined || hidden !== undefined;
    // Only the choices the update makes; the preference keeps the rest.
    const preference = {
      businessId: project.business_id,
      projectId: project.id,
      ...(favorite !== undefined ? { favorite } : {}),
      ...(hidden !== undefined ? { hidden } : {}),
      ...(sensorDisplay !== undefined ? { sensorDisplay } : {}),
      ...(dashboardLayout !== undefined ? { dashboardLayout } : {}),
      entityType: 'USER_PROJECT_PREFERENCE',
    };

    const existingPreference =
      getState().projects.userProjectPreferences?.[
        getUserProjectPreferenceKey(preference)
      ];

    const mergedPreference = {
      ...existingPreference,
      ...preference,
      createdAt: existingPreference?.createdAt || now,
      updatedAt: (!movesInProjectList && existingPreference?.updatedAt) || now,
    };

    const cacheKey = movesInProjectList
      ? getProjectsActivityFilterCacheKey(activeEmployerInfo)
      : null;
    const latestProjectIds =
      (cacheKey &&
        getState().projects.latestUserActiveProjectIdsByEmployer?.[cacheKey]) ||
      [];

    dispatch(
      applyOptimisticUserProjectPreferenceUpdate({
        requestId,
        cacheKey,
        previousPreference: existingPreference || null,
        previousLatestProjectIds: [...latestProjectIds],
        preference: mergedPreference,
      })
    );

    try {
      return await patchUserProjectPreference({
        preference: mergedPreference,
        activeEmployerInfo,
      });
    } catch (error) {
      const data = error?.response?.data;
      return rejectWithValue({
        status: error?.response?.status ?? null,
        message:
          (typeof data === 'string' && data) ||
          data?.message ||
          data?.error ||
          error?.message ||
          'Saving the project preference failed',
      });
    }
  }
);
